/**
 * Explore date-picker catalog — rolling windows and retail season brackets.
 *
 * Shared by the web picker and MCP so “Q4 holiday season” resolves to the same
 * calendar pair on both surfaces. The leftover `WORKSPACE_DATE_RANGES` token
 * stays on workspace filter schemas; this module is how a named preset becomes
 * a closed `{ startDate, endDate }` window.
 */

import { WORKSPACE_DATE_RANGES } from '../events/workspace.js';
import {
  isAcceptableDateWindow,
  trailingWindowFromDaysBack,
  type DateWindow,
} from './date-window.js';

export const CUSTOM_PRESET_ID = 'custom';

/** Schema placeholder when a preset has no legacy token. Never used as the query window. */
export const PLACEHOLDER_DATE_RANGE_TOKEN = '90d' satisfies (typeof WORKSPACE_DATE_RANGES)[number];

export interface ExploreRollingPreset {
  id: string;
  /** Inclusive days ending today. */
  days: number;
  defaultLabel: string;
}

export interface ExploreSeasonPreset {
  id: string;
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
  /** True when the bracket opens in December and closes in the next January. */
  spansYearBoundary?: boolean;
  defaultLabel: string;
}

export const ROLLING_PRESETS = [
  { id: 'last_7_days', days: 7, defaultLabel: 'Last 7 days' },
  { id: 'last_14_days', days: 14, defaultLabel: 'Last 14 days' },
  { id: 'last_30_days', days: 30, defaultLabel: 'Last 30 days' },
  { id: 'last_90_days', days: 90, defaultLabel: 'Last 90 days' },
  { id: 'last_year', days: 365, defaultLabel: 'Last year' },
] as const satisfies readonly ExploreRollingPreset[];

/**
 * Retail season brackets as fixed month/day bounds.
 *
 * Labor Day and Prime Big Deal Days shift year to year, but every bracket is two
 * to four weeks wide, so a few days of drift does not change the analysis.
 */
export const SEASON_PRESETS = [
  {
    // The umbrella bracket: the union of Halloween, Singles' Day, Black Friday /
    // Cyber Monday, Christmas gifting, and New Year. Its bounds are the earliest
    // start and latest end across those five. Fall Prime Big Deal Days sits outside it.
    id: 'q4_holiday',
    startMonth: 10,
    startDay: 10,
    endMonth: 1,
    endDay: 15,
    spansYearBoundary: true,
    defaultLabel: 'Q4 holiday season',
  },
  {
    id: 'back_to_school',
    startMonth: 7,
    startDay: 15,
    endMonth: 9,
    endDay: 8,
    defaultLabel: 'Back to School',
  },
  {
    id: 'labor_day',
    startMonth: 8,
    startDay: 17,
    endMonth: 9,
    endDay: 10,
    defaultLabel: 'Labor Day sales',
  },
  {
    id: 'fall_prime',
    startMonth: 9,
    startDay: 16,
    endMonth: 10,
    endDay: 11,
    defaultLabel: 'Fall Prime Big Deal Days',
  },
  {
    id: 'halloween',
    startMonth: 10,
    startDay: 10,
    endMonth: 11,
    endDay: 3,
    defaultLabel: 'Halloween',
  },
  {
    id: 'singles_day',
    startMonth: 10,
    startDay: 21,
    endMonth: 11,
    endDay: 14,
    defaultLabel: "Singles' Day 11.11",
  },
  {
    id: 'black_friday',
    startMonth: 11,
    startDay: 6,
    endMonth: 12,
    endDay: 3,
    defaultLabel: 'Black Friday / Cyber Monday',
  },
  {
    id: 'christmas_gifting',
    startMonth: 12,
    startDay: 4,
    endMonth: 12,
    endDay: 26,
    defaultLabel: 'Christmas gifting',
  },
  {
    id: 'new_year',
    startMonth: 12,
    startDay: 11,
    endMonth: 1,
    endDay: 15,
    spansYearBoundary: true,
    defaultLabel: 'New Year',
  },
] as const satisfies readonly ExploreSeasonPreset[];

export type RollingPresetId = (typeof ROLLING_PRESETS)[number]['id'];
export type SeasonPresetId = (typeof SEASON_PRESETS)[number]['id'];
export type CatalogPresetId = RollingPresetId | SeasonPresetId;
export type ExplorePresetId = CatalogPresetId | typeof CUSTOM_PRESET_ID;
export type WorkspaceDateRangeToken = (typeof WORKSPACE_DATE_RANGES)[number];

export const EXPLORE_DATE_PRESET_IDS = [
  'last_7_days',
  'last_14_days',
  'last_30_days',
  'last_90_days',
  'last_year',
  'q4_holiday',
  'back_to_school',
  'labor_day',
  'fall_prime',
  'halloween',
  'singles_day',
  'black_friday',
  'christmas_gifting',
  'new_year',
] as const satisfies readonly CatalogPresetId[];

const LEGACY_TOKEN_BY_PRESET_ID: Record<string, WorkspaceDateRangeToken> = {
  last_7_days: '7d',
  last_30_days: '30d',
  last_90_days: '90d',
  last_year: '12m',
};

const PRESET_ID_BY_LEGACY_TOKEN = {
  '7d': 'last_7_days',
  '30d': 'last_30_days',
  '90d': 'last_90_days',
  '12m': 'last_year',
} as const;

export interface ExploreDatePresetOption {
  id: CatalogPresetId;
  label: string;
  startDate: string;
  endDate: string;
}

export interface InsightDateWindowInput {
  dateRange?: string;
  startDate?: string;
  endDate?: string;
}

export type DateRangeClarifyReason = 'missing' | 'unclear';

export type InsightDateWindowResult =
  | {
      ok: true;
      window: DateWindow;
      leftoverToken: WorkspaceDateRangeToken;
      presetId?: ExplorePresetId;
      presetLabel?: string;
    }
  | { ok: false; reason: DateRangeClarifyReason };

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function toYmd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function todayYmdUtc(today: Date): string {
  return toYmd(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate());
}

export function isCatalogPresetId(presetId: string): presetId is CatalogPresetId {
  return (
    ROLLING_PRESETS.some((preset) => preset.id === presetId) ||
    SEASON_PRESETS.some((preset) => preset.id === presetId)
  );
}

export function isExplorePresetId(presetId: string): presetId is ExplorePresetId {
  return presetId === CUSTOM_PRESET_ID || isCatalogPresetId(presetId);
}

export function isLegacyDateRangeToken(value: string): value is WorkspaceDateRangeToken {
  return (WORKSPACE_DATE_RANGES as readonly string[]).includes(value);
}

export function catalogPresetLabel(presetId: string): string | undefined {
  const rolling = ROLLING_PRESETS.find((preset) => preset.id === presetId);
  if (rolling) return rolling.defaultLabel;
  const season = SEASON_PRESETS.find((preset) => preset.id === presetId);
  return season?.defaultLabel;
}

/** The leftover workspace token for a preset, or undefined when it has none. */
export function presetIdToLegacyToken(presetId: string): WorkspaceDateRangeToken | undefined {
  return LEGACY_TOKEN_BY_PRESET_ID[presetId];
}

/**
 * Inverse of `presetIdToLegacyToken`. Unknown tokens return undefined — callers
 * that need a web default apply one themselves. MCP must not.
 */
export function legacyTokenToPresetId(token: string): CatalogPresetId | undefined {
  if (token in PRESET_ID_BY_LEGACY_TOKEN) {
    return PRESET_ID_BY_LEGACY_TOKEN[token as keyof typeof PRESET_ID_BY_LEGACY_TOKEN];
  }
  return undefined;
}

/**
 * The occurrence of `season` whose end date is on or before `today` (UTC calendar).
 *
 * A boundary-spanning bracket is anchored on its end year, so the New Year
 * bracket ending 15 January resolves its start to the preceding December.
 */
export function resolveSeasonOccurrence(
  season: ExploreSeasonPreset,
  today: Date = new Date(),
): DateWindow {
  const todayStr = todayYmdUtc(today);
  const year = today.getUTCFullYear();

  for (let endYear = year + 1; ; endYear -= 1) {
    const endDate = toYmd(endYear, season.endMonth, season.endDay);
    if (endDate > todayStr) continue;
    const startYear = season.spansYearBoundary ? endYear - 1 : endYear;
    return {
      startDate: toYmd(startYear, season.startMonth, season.startDay),
      endDate,
    };
  }
}

/**
 * Closed window for a catalog preset id or a leftover `7d|30d|90d|12m` token.
 * `custom` and unknown ids return null.
 */
export function resolveExplorePresetWindow(
  presetId: string,
  today: Date = new Date(),
): DateWindow | null {
  const catalogId = isCatalogPresetId(presetId) ? presetId : legacyTokenToPresetId(presetId);
  if (!catalogId) return null;

  const rolling = ROLLING_PRESETS.find((preset) => preset.id === catalogId);
  if (rolling) return trailingWindowFromDaysBack(rolling.days, today);

  const season = SEASON_PRESETS.find((preset) => preset.id === catalogId);
  if (season) return resolveSeasonOccurrence(season, today);

  return null;
}

export function leftoverDateRangeToken(presetOrToken: string | undefined): WorkspaceDateRangeToken {
  if (!presetOrToken) return PLACEHOLDER_DATE_RANGE_TOKEN;
  if (isLegacyDateRangeToken(presetOrToken)) return presetOrToken;
  return presetIdToLegacyToken(presetOrToken) ?? PLACEHOLDER_DATE_RANGE_TOKEN;
}

export function listExploreDatePresets(today: Date = new Date()): ExploreDatePresetOption[] {
  return EXPLORE_DATE_PRESET_IDS.map((id) => {
    const window = resolveExplorePresetWindow(id, today);
    if (!window) {
      throw new Error(`Explore catalog id ${id} must resolve to a window`);
    }
    return {
      id,
      label: catalogPresetLabel(id) ?? id,
      startDate: window.startDate,
      endDate: window.endDate,
    };
  });
}

/**
 * Resolve a chat-supplied window. A valid `YYYY-MM-DD` pair wins. A catalog
 * preset or leftover token resolves next. Missing or unusable input is
 * `missing` / `unclear` — never a silent fallback.
 */
export function resolveInsightDateWindow(
  input: InsightDateWindowInput,
  today: Date = new Date(),
): InsightDateWindowResult {
  const startDate = input.startDate?.trim() ?? '';
  const endDate = input.endDate?.trim() ?? '';
  const dateRange = input.dateRange?.trim() ?? '';
  const hasStart = startDate.length > 0;
  const hasEnd = endDate.length > 0;

  if (hasStart || hasEnd) {
    if (!isAcceptableDateWindow(startDate, endDate)) {
      return { ok: false, reason: 'unclear' };
    }
    return {
      ok: true,
      window: { startDate, endDate },
      leftoverToken: leftoverDateRangeToken(dateRange),
      presetId: CUSTOM_PRESET_ID,
      presetLabel: 'Custom range',
    };
  }

  if (dateRange.length === 0) {
    return { ok: false, reason: 'missing' };
  }

  if (dateRange === CUSTOM_PRESET_ID) {
    return { ok: false, reason: 'unclear' };
  }

  const window = resolveExplorePresetWindow(dateRange, today);
  if (!window) {
    return { ok: false, reason: 'unclear' };
  }

  const presetId = isCatalogPresetId(dateRange) ? dateRange : legacyTokenToPresetId(dateRange);
  return {
    ok: true,
    window,
    leftoverToken: leftoverDateRangeToken(dateRange),
    ...(presetId && { presetId, presetLabel: catalogPresetLabel(presetId) }),
  };
}
